import React from "react";
import {
  Box,
  Card,
  Typography,
  Avatar,
  IconButton,
  Stack,
  TextField,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import KitchenIcon from "@mui/icons-material/Kitchen";
import { removeOfferFromOrder } from "../../../../../../../../services/pos/reservation/reservationApi";
import { usePermissions } from "../../../../../../../../context/PermissionsAndFeatures/UserPermissionsContext";

const OrderItemsList = ({
  items,
  scrollThreshold = 4,
  onDeleteItem,
  onEditItem,
  onMoveItem,
  preparationCasherEnabled = false,
  onPrepareItem = () => {},
  changeOrderItemStatus = null,
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [menuItemId, setMenuItemId] = React.useState(null);

  // حالة عرض تفاصيل الصنف
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [detailsItem, setDetailsItem] = React.useState(null);
  const { hasPermission } = usePermissions();

  // mobile bottom-sheet state
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [mobileMenuItem, setMobileMenuItem] = React.useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleMenuOpen = (event, itemId, item) => {
    if (isMobile) {
      setMobileMenuItem(item || null);
      setMobileMenuOpen(true);
      // still set anchor for desktop fallback if needed
      setMenuAnchor(event.currentTarget);
      setMenuItemId(itemId);
    } else {
      setMenuAnchor(event.currentTarget);
      setMenuItemId(itemId);
    }
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuItemId(null);
    setMobileMenuOpen(false);
    setMobileMenuItem(null);
  };

  const handleShowDetails = (item) => {
    setDetailsItem(item);
    setDetailsOpen(true);
    handleMenuClose();
  };

  // معالج حذف العرض مباشرة عبر API الخاص بـ reservation
  const handleRemoveOffer = async (item) => {
    const orderId = item.order_id || item.orderId;
    if (!orderId) return alert("❌ لا يمكن حذف العرض: معرف الطلب مفقود.");
    if (!window.confirm("❗ هل تريد حذف هذا العرض من الطلب؟")) return;

    try {
      await removeOfferFromOrder(orderId, item.id);
      alert("✅ تم حذف العرض من الطلب بنجاح");

      // إشعار لباقي الأجزاء (مثل TablesSection / OrderDetailsFetcher) لإعادة التحميل
      window.dispatchEvent(
        new CustomEvent("orderUpdated", {
          detail: { orderId, tableId: item.table_id || null },
        })
      );
    } catch (err) {
      alert(err.message || "❌ فشل في حذف العرض");
    }
  };

  if (!items || items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        لا توجد عناصر في هذا الطلب.
      </Typography>
    );
  }

  const subdomain = window.location.hostname.split(".")[0];
  const IMAGE_BASE_URL = `https://${subdomain}.restocash.app/`;
  const placeholderImage = "https://via.placeholder.com/300x140?text=No+Image";

  // دالة مساعدة لتحديد لون ونص الحالة
  const getStatusInfo = (status) => {
    switch (status) {
      case "finished":
        return { label: "تم الانتهاء", color: "#4caf50", bgcolor: "#e8f5e9" };
      case "in_prepration":
        return { label: "تم الإرسال للتجهيز", color: "#fb8c00", bgcolor: "#fff3e0" };
      case "pending":
        return { label: "قيد التجهيز", color: "#ff9800", bgcolor: "#fff8e1" };
      case "under_review":
        return { label: "قيد المراجعة", color: "#2196f3", bgcolor: "#e3f2fd" };
      default:
        return { label: "قيد التجهيز", color: "#9e9e9e", bgcolor: "#f5f5f5" };
    }
  };

  return (
    <Box mt={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 1.5,
        }}
      >
        {items.map((item) => {
          const statusInfo = getStatusInfo(item.status);
          
          return (
            <Card
              key={item.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                bgcolor: "#232334",
                borderRadius: 2,
                p: 1.5,
                boxShadow: 2,
                position: "relative",
                border: `1px solid ${statusInfo.bgcolor}30`,
              }}
              elevation={0}
            >
              {/* الصف الأول: الصورة + البيانات الأساسية */}
              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
                {/* صورة المنتج + أيقونة العرض */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mr: 1.5,
                  }}
                >
                  <Avatar
                    src={
                      item.product_image
                        ? `${IMAGE_BASE_URL}${item.product_image.replace(
                            / /g,
                            "%20"
                          )}`
                        : placeholderImage
                    }
                    alt={item.product_name}
                    sx={{ width: 56, height: 56, borderRadius: 1.5 }}
                  />
                  {item.is_offer === 1 && (
                    <Tooltip title="عرض محتوى العرض">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDetailsItem(item);
                          setDetailsOpen(true);
                        }}
                        sx={{ mt: 0.5, color: "#fbc2eb" }}
                      >
                        <LocalOfferIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {/* بيانات الصنف */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: "#fff",
                      fontSize: 15,
                      mb: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {item.product_name}
                  </Typography>

                  {/* معلومات السعر والكمية */}
                  <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        bgcolor: "#1a1a2e",
                        borderRadius: 1,
                        px: 1,
                        py: 0.3,
                      }}
                    >
                      <Typography variant="caption" color="#b0bec5" sx={{ mr: 0.5 }}>
                        الكمية:
                      </Typography>
                      <Typography variant="caption" color="#fff" fontWeight={600}>
                        {item.quantity}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="#b0bec5">
                      السعر: ${item.price}
                    </Typography>
                  </Stack>

                  {/* الإجمالي */}
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: "#fbc2eb", fontSize: 16 }}
                  >
                    الإجمالي: ${item.total}
                  </Typography>
                </Box>

                {/* أزرار الإجراءات */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {/* زر القائمة */}
                  <IconButton
                    color="primary"
                    size="small"
                    sx={{
                      border: "1px solid rgba(255,255,255,0.06)",
                      bgcolor: "#1a1a2e",
                      width: 32,
                      height: 32,
                    }}
                    onClick={(e) => handleMenuOpen(e, item.id, item)}
                  >
                    <MoreVertIcon sx={{ color: "#fff", fontSize: 18 }} />
                  </IconButton>

                  {/* زر التجهيز */}
                  {preparationCasherEnabled && item.status === "pending" && (
                    <Tooltip title="وضع الصنف قيد التجهيز">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (typeof changeOrderItemStatus === "function") {
                            changeOrderItemStatus(item.id);
                          } else {
                            onPrepareItem(item);
                          }
                        }}
                        sx={{
                          bgcolor: "#fff0f6",
                          color: "#f48fb1",
                          width: 32,
                          height: 32,
                        }}
                      >
                        <KitchenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {item.status === "preparing" && (
                    <Tooltip title="قيد التجهيز">
                      <IconButton
                        size="small"
                        sx={{
                          bgcolor: "#e8f5e9",
                          color: "#388e3c",
                          width: 32,
                          height: 32,
                        }}
                        disabled
                      >
                        <KitchenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {item.status === "in_prepration" && (
                    <Tooltip title="تم الإرسال للتجهيز">
                      <IconButton
                        size="small"
                        sx={{
                          bgcolor: "#fff3e0",
                          color: "#fb8c00",
                          width: 32,
                          height: 32,
                        }}
                        disabled
                      >
                        <KitchenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {/* الصف الثاني: الحالة والملاحظات */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.8,
                  pt: 1,
                  borderTop: "1px solid rgba(255,255,255,0.05)",
                }}
              >
                {/* عرض الحالة */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="caption" color="#b0bec5" sx={{ minWidth: 60 }}>
                    الحالة:
                  </Typography>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      bgcolor: statusInfo.bgcolor,
                      color: statusInfo.color,
                      px: 1.5,
                      py: 0.4,
                      borderRadius: 1.5,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {statusInfo.label}
                  </Box>
                </Box>

                {/* عرض الملاحظة */}
                {item.note && item.note.trim() !== "" && (
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Typography variant="caption" color="#b0bec5" sx={{ minWidth: 60, mt: 0.3 }}>
                      ملاحظة:
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        bgcolor: "#1a1a2e",
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.8,
                        borderLeft: "3px solid #fbc2eb",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#fff",
                          fontSize: 13,
                          lineHeight: 1.5,
                          display: "block",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.note}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Card>
          );
        })}
      </Box>

      {/* Desktop Menu (unchanged behavior) */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor) && !isMobile}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          "& .MuiMenu-paper": { borderRadius: 2, minWidth: 160, boxShadow: 3 },
        }}
      >
        <MenuItem
          onClick={() => {
            handleShowDetails(items.find((i) => i.id === menuItemId));
          }}
        >
          تفاصيل الصنف
        </MenuItem>
        {hasPermission("orders_tables_print_table") && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onEditItem?.(items.find((i) => i.id === menuItemId));
            }}
          >
            <EditIcon sx={{ color: "#1976d2", mr: 1 }} fontSize="small" /> تعديل
          </MenuItem>
        )}
        {hasPermission("orders_other_delete_items") && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              const it = items.find((i) => i.id === menuItemId);
              if (!it) return;
              if (it.is_offer === 1) handleRemoveOffer(it);
              else onDeleteItem?.(menuItemId);
            }}
            sx={{ color: "#ff4d4f" }}
          >
            <DeleteIcon sx={{ color: "#ff4d4f", mr: 1 }} fontSize="small" /> حذف
          </MenuItem>
        )}
        {hasPermission("orders_other_edite_price") && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onMoveItem?.(items.find((i) => i.id === menuItemId));
            }}
          >
            <SwapHorizIcon sx={{ color: "#1976d2", mr: 1 }} fontSize="small" />{" "}
            نقل
          </MenuItem>
        )}
      </Menu>

      {/* Mobile bottom-sheet menu */}
      <Dialog
        open={mobileMenuOpen}
        onClose={handleMenuClose}
        fullWidth
        sx={{ "& .MuiDialog-container": { alignItems: "flex-end" } }}
      >
        <DialogTitle>إجراءات الصنف</DialogTitle>
        <DialogContent>
          {mobileMenuItem && (
            <>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                {mobileMenuItem.product_name}
              </Typography>
              <Stack spacing={1}>
                <Button
                  variant="text"
                  onClick={() => {
                    handleShowDetails(mobileMenuItem);
                  }}
                >
                  تفاصيل الصنف
                </Button>
                {hasPermission("orders_tables_print_table") && (
                  <Button
                    variant="text"
                    onClick={() => {
                      handleMenuClose();
                      onEditItem?.(mobileMenuItem);
                    }}
                  >
                    <EditIcon sx={{ mr: 1 }} /> تعديل
                  </Button>
                )}
                {hasPermission("orders_other_delete_items") && (
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => {
                      handleMenuClose();
                      if (mobileMenuItem.is_offer === 1)
                        handleRemoveOffer(mobileMenuItem);
                      else onDeleteItem?.(mobileMenuItem.id);
                    }}
                  >
                    <DeleteIcon sx={{ mr: 1 }} /> حذف
                  </Button>
                )}
                {hasPermission("orders_other_edite_price") && (
                  <Button
                    variant="text"
                    onClick={() => {
                      handleMenuClose();
                      onMoveItem?.(mobileMenuItem);
                    }}
                  >
                    <SwapHorizIcon sx={{ mr: 1 }} /> نقل
                  </Button>
                )}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMenuClose}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog لعرض تفاصيل الصنف */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>تفاصيل الصنف</DialogTitle>
        <DialogContent>
          {detailsItem && (
            <>
              {/* إذا كان العنصر عرض - نعرض قائمة الأصناف الداخلة */}
              {detailsItem.is_offer === 1 &&
              Array.isArray(detailsItem.items) ? (
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    {detailsItem.product_name} (عرض)
                  </Typography>
                  {detailsItem.items.map((it) => (
                    <Box
                      key={it.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Avatar
                        src={
                          it.product_image
                            ? `${IMAGE_BASE_URL}${it.product_image.replace(
                                / /g,
                                "%20"
                              )}`
                            : placeholderImage
                        }
                        alt={it.product_name}
                        sx={{ width: 56, height: 56 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {it.product_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          الكمية: {it.quantity} — السعر: ${it.price} — الإجمالي:
                          ${it.total}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                /* عرض تفاصيل صنف واحد عادي */
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    {detailsItem.product_name}
                  </Typography>
                  <Avatar
                    src={
                      detailsItem.product_image
                        ? `${IMAGE_BASE_URL}${detailsItem.product_image.replace(
                            / /g,
                            "%20"
                          )}`
                        : placeholderImage
                    }
                    alt={detailsItem.product_name}
                    sx={{ width: 80, height: 80, mb: 2, mx: "auto" }}
                  />
                  <Typography variant="body2" mb={1}>
                    <strong>الكمية:</strong> {detailsItem.quantity}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>السعر:</strong> ${detailsItem.price}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>الإجمالي:</strong> ${detailsItem.total}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>ملاحظة:</strong> {detailsItem.note || "لا يوجد"}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>الحالة:</strong>{" "}
                    {detailsItem.status === "finished"
                      ? "تم الانتهاء"
                      : detailsItem.status === "in_prepration"
                      ? "تم الإرسال للتجهيز"
                      : detailsItem.status === "preparing"
                      ? "قيد التجهيز"
                      : detailsItem.status === "under_review"
                      ? "طلب خارجي قيد المراجعة"
                      : "قيد التجهيز"}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)} color="primary">
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderItemsList;
